import React, { useState } from 'react';
import { X, Send, AlertCircle, CheckCircle, Loader } from 'lucide-react';

interface SendCampaignNowModalProps {
  campaignId: string;
  campaignSubject: string;
  subscriberCount: number;
  scheduledDate: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface SendResult {
  success: boolean;
  sent: number;
  failed: number;
  total: number;
  success_rate: number;
  campaign_status: string;
  error?: string;
}

export function SendCampaignNowModal({
  campaignId,
  campaignSubject,
  subscriberCount,
  scheduledDate,
  onClose,
  onSuccess,
}: SendCampaignNowModalProps) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendResult | null>(null);

  const handleSend = async () => {
    setSending(true);
    setResult(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-scheduled-campaign-now`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            campaign_id: campaignId,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setResult({
          success: false,
          sent: 0,
          failed: 0,
          total: 0,
          success_rate: 0,
          campaign_status: 'failed',
          error: data.error || 'Er ging iets mis bij het versturen',
        });
        return;
      }

      setResult({
        success: true,
        sent: data.sent,
        failed: data.failed,
        total: data.total,
        success_rate: data.success_rate,
        campaign_status: data.campaign_status,
      });

      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (error) {
      setResult({
        success: false,
        sent: 0,
        failed: 0,
        total: 0,
        success_rate: 0,
        campaign_status: 'failed',
        error: error instanceof Error ? error.message : 'Onbekende fout',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Campaign Nu Versturen</h2>
          <button
            onClick={onClose}
            disabled={sending}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {!result && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-amber-900 mb-1">Let op!</h3>
                    <p className="text-sm text-amber-800">
                      Deze campaign wordt <strong>onmiddellijk</strong> verstuurd naar de geabonneerde contacten (volgens de doelgroep van de campagne).
                      Deze actie kan niet ongedaan gemaakt worden.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-sm font-medium text-gray-700">Subject:</span>
                  <span className="text-sm text-gray-900 text-right max-w-xs truncate">
                    {campaignSubject}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Aantal ontvangers:</span>
                  <span className="text-sm font-semibold text-gray-900">{subscriberCount}</span>
                </div>

                {scheduledDate && (
                  <div className="flex justify-between items-start">
                    <span className="text-sm font-medium text-gray-700">Oorspronkelijk gepland:</span>
                    <span className="text-sm text-gray-600">
                      {new Date(scheduledDate).toLocaleString('nl-NL')}
                    </span>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-600">
                  Emails worden verstuurd in batches van 100 met een korte delay tussen elke batch.
                  Dit proces kan enkele minuten duren afhankelijk van het aantal ontvangers.
                </p>
              </div>
            </>
          )}

          {sending && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader className="w-12 h-12 text-blue-600 animate-spin" />
              <div className="text-center space-y-2">
                <p className="text-lg font-medium text-gray-900">Emails worden verstuurd...</p>
                <p className="text-sm text-gray-600">
                  Dit kan enkele minuten duren. Sluit dit venster niet.
                </p>
              </div>
            </div>
          )}

          {result && !sending && (
            <div className="space-y-4">
              {result.success ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-medium text-green-900 mb-1">Campaign verstuurd!</h3>
                      <p className="text-sm text-green-800">
                        De campaign is succesvol verstuurd naar je contacten.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-medium text-red-900 mb-1">Fout bij versturen</h3>
                      <p className="text-sm text-red-800">
                        {result.error || 'Er ging iets mis bij het versturen van de campaign.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {result.total > 0 && (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Succesvol verzonden:</span>
                    <span className="text-sm font-semibold text-green-600">{result.sent}</span>
                  </div>

                  {result.failed > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Mislukt:</span>
                      <span className="text-sm font-semibold text-red-600">{result.failed}</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Totaal verwerkt:</span>
                    <span className="text-sm font-semibold text-gray-900">{result.total}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Success rate:</span>
                    <span className={`text-sm font-semibold ${
                      result.success_rate >= 95 ? 'text-green-600' :
                      result.success_rate >= 50 ? 'text-amber-600' :
                      'text-red-600'
                    }`}>
                      {result.success_rate.toFixed(1)}%
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Campaign status:</span>
                    <span className={`text-sm font-semibold ${
                      result.campaign_status === 'completed' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {result.campaign_status === 'completed' ? 'Voltooid' : 'Mislukt'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          {!result && (
            <>
              <button
                onClick={onClose}
                disabled={sending}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuleren
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Versturen...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Ja, Verstuur Nu
                  </>
                )}
              </button>
            </>
          )}
          {result && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800"
            >
              Sluiten
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
